import {
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  Card,
  CrudMode,
  DataDisplay,
  Grid,
  GridBreak,
  Page,
  PageRoute,
  useElementBreakpoints,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { FilePanel, Input } from "components";
import { Permission, Survey, SurveyViewResource, UploadFile } from "generated/openApi";
import produce from "immer";
import "moment-timezone";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { SurveyReportPageHeader } from "./SurveyReportPageHeader";

const routeName = "surveyReport";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
}

class OptionalParams {
  test: any;
}

export const surveyReportPageRoute: PageRoute<Params, OptionalParams> = {
  handle: "Survey Report",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
  getOptionalParams: (params) => buildOptionalParams(params),
};


const ROOT_PERMISSION: Permission = Permission.Form;

export const SurveyReportPage = () => {
  const { params, optionalParams } = useParams(routeName, Params);
  const navigate = useNavigate();
  const [surveyReport, setSurveyReport] = useState<Survey>({} as Survey);
  const [refreshSurvey, setRefreshSurvey] = useState<Survey>();
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [surveyReport] });
  const [viewResource, setViewResource] = useState<SurveyViewResource>({} as SurveyViewResource);
  const { validationErrors, setValidationErrors } = useValidationErrors();
  const { breakpointsRef, setBreakpoints, fullFieldBreakpoints } = useElementBreakpoints();

  useEffect(() => {
    fetchSurvey(params.id);
    fetchViewResource(params.id);
  }, []);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(`../${surveyReportPageRoute.getPath({ id: id, crudMode: crudMode })}`);

  const fetchRefreshSurvey = (id: number) => {
    ApiService.surveyReports.getSurveyReport(id).then((updatedSurvey) => {
      setRefreshSurvey(updatedSurvey);
      resetUnsavedChanges([updatedSurvey]);
    });
  };

  const fetchSurvey = (id: number) => {
    ApiService.surveyReports.getSurveyReport(id).then((response) => {
      setSurveyReport(response);
      setRefreshSurvey(response);
      resetUnsavedChanges([response]);
    });
  };

  const fetchViewResource = (id: number) => {
    ApiService.surveys.getViewResource(id).then((response) => {
      setViewResource(response);
    });
  };

  return (
    <Page
      isForm
      header={
        <SurveyReportPageHeader
          rootPermission={ROOT_PERMISSION}
          crudMode={params.crudMode}
          fetch={fetchSurvey}
          fetchRefreshSurvey={fetchRefreshSurvey}
          reroute={reroute}
          surveyReport={surveyReport}
          setValidationErrors={setValidationErrors}
          unsavedChanges={unsavedChanges}
        />
      }
    >

      <Card sx={{ maxWidth: "500px" }}>
        <Grid ref={breakpointsRef} container spacing="sm">
          <Grid item xs={setBreakpoints({ md: 4, xs: 12 })}>
            <FilePanel
              fileName={surveyReport?.uploadFile?.fileName}
              fileRef={surveyReport?.uploadFile?.fileRef}
              fileSize={surveyReport?.uploadFile?.fileSize}
              uploadingData={surveyReport?.uploadFile?.uploadingData}
              imagePreviewData={surveyReport?.uploadFile?.imagePreviewData}
              onFileChange={(fileName, fileSize, uploadingData) => {
                setSurveyReport(
                  produce(surveyReport, (draft) => {
                    draft.uploadFile = {
                      fileName: fileName,
                      fileSize: fileSize,
                      uploadingData: uploadingData,
                      touched: true,
                    } as UploadFile;
                  })
                );
              }}
              // errors={props.validationErrors[pn<Import>("uploadFile")]?.[pn<UploadFile2>("fileName")]}
            />

          </Grid>

          <GridBreak />

          <Grid item xs={setBreakpoints({ md: 9, xs: 12 })}>
            {params.crudMode === "view" ? (
              <DataDisplay label="Name">
                {surveyReport?.name}
              </DataDisplay>
            ) : (
              <Input
                label="Name"
                value={surveyReport?.name}
                onChange={(e) =>
                  setSurveyReport(
                    produce(surveyReport, (draft) => {
                      draft!.name = e.target.value;
                    })
                  )
                }
              // errors={props.validationErrors[pn<Import>("importFileId")]}
              />
            )}
          </Grid>

        </Grid>
      </Card>

    </Page>
  );
};
